import { useMemo } from 'react'
import { BarChartWrapper } from '../../charts/BarChart'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { formatMoney } from '../../format'
import { runExport, type ExportColumn } from '../../../../lib/export'
import { aggregateCertifiedByJurisdiction } from '../../reportsData'
import { buildTopNWithOther, type TopNRow } from './analyticsMappers'
import type { CreditValueByJurisdictionResult } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.creditValueByJurisdictionFy

/** Same disclosed constant as every other certified-value tile — every certified-value fact at launch is USD. */
const REPORTING_CURRENCY = 'USD'

const TOP_N = 5
const OTHER_KEY = '__other__'

const EXPORT_COLUMNS: ExportColumn<TopNRow>[] = [
  { id: 'jurisdiction', header: 'Jurisdiction', toText: (r) => r.label },
  { id: 'total', header: `Certified total (${REPORTING_CURRENCY})`, toText: (r) => formatMoney(String(r.total), REPORTING_CURRENCY) },
]

/**
 * AC1.4 — Top jurisdictions by certified-value SUM (top 5, descending) plus a 6th "All other" bar
 * when more than 5 jurisdictions have nonzero value. Reuses the EXISTING `credit-value-by-
 * jurisdiction-fy` payload, client-aggregated exactly as `reportsData.ts#aggregateCertifiedByJurisdiction`
 * already does for `CreditsByJurisdictionStatusWidget` — zero new backend read.
 */
export function TopJurisdictionsWidget({ filter, skipRefetch, onCrossFilter, onDrill }: WidgetProps) {
  const { status, data, message, retry } =
    useWidgetData<CreditValueByJurisdictionResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const totals = useMemo(() => aggregateCertifiedByJurisdiction(buckets), [buckets])
  const topN = useMemo(
    () => buildTopNWithOther(totals.map((t) => ({ key: t.jurisdiction, label: t.label, total: t.total })), TOP_N),
    [totals],
  )

  const rows: TopNRow[] =
    topN.otherCount > 0 ? [...topN.rows, { key: OTHER_KEY, label: `All other (${topN.otherCount})`, total: topN.otherTotal }] : topN.rows

  const frameState = status === 'ok' && rows.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Top jurisdictions by certified value"
      icon="bar-chart"
      state={frameState}
      message={message ?? 'No jurisdiction has certified credit yet for the current filter.'}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={rows.length > 0 ? { onClick: () => runExport('csv', 'Top jurisdictions by certified value', rows, EXPORT_COLUMNS) } : undefined}
    >
      <BarChartWrapper<TopNRow>
        title="Top jurisdictions by certified value"
        data={rows}
        categoryKey="label"
        categoryLabel="Jurisdiction"
        series={[{ key: 'total', label: 'Certified value' }]}
        valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
        onBarClick={(row) => {
          if (row.key !== OTHER_KEY) onCrossFilter('jurisdiction', row.key)
        }}
      />
    </WidgetFrame>
  )
}
